// DTOs de C3 — programación semanal de supervisores (coordinador).
//
//   - GET /api/coordinador/programacion-supervisores?semana_iso=YYYY-Www
//   - POST /api/coordinador/programacion-supervisores
//   - DELETE /api/coordinador/programacion-supervisores/:id
//   - POST /api/coordinador/programacion-supervisores/copiar-semana
//
// Scope: coordinador solo programa supervisores activos del tenant. Los 3
// supervisores rotan T1/T2/T3 y cubren toda la planta (no se asignan a línea
// específica como los líderes).
//
// Diferencias clave con C2:
//   - 1 supervisor por (fecha, turno) — UNIQUE BD lo enforce (no hay linea_id)
//   - Mismo supervisor en 2 turnos del mismo día = OK (doble turno cuando
//     otro falta) — NO bloqueamos a nivel BD/POST
//   - "Conflicto" en UI = doble turno (visual, NO bloqueante)
//   - Grid más simple: filas turno (T1/T2/T3) × columnas días (lun-dom)
import { z } from 'zod';
const semanaIsoRegex = /^[0-9]{4}-W[0-9]{2}$/;
const horaTimeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
// =============================================================================
// Recursos del scope (supervisores activos, turnos)
// =============================================================================
export const supervisorResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
    email: z.string().email(),
    /** Iniciales 2 letras (primera del nombre + primera del apellido), mayúsculas. */
    iniciales: z.string().min(1).max(4),
});
const turnoResumenSchema = z.object({
    id: z.string().uuid(),
    // BD permite 1-4 (CHECK constraint). T4 es opcional en plantas con turno extra.
    numero: z.number().int().min(1).max(4),
    nombre: z.string(),
    hora_inicio: z.string().regex(horaTimeRegex),
    hora_fin: z.string().regex(horaTimeRegex),
    cruza_medianoche: z.boolean(),
});
// =============================================================================
// Horario resuelto por (turno, fecha) de la semana — design horario-turno-dia
// §5: el FE lo usa para deshabilitar celdas de días "No opera" (S5b). Clon del
// de C2 (no se cross-importa entre schemas de c2 y c3, mismo patrón del resto
// del archivo).
// =============================================================================
export const horarioPorFechaSupervisoresSchema = z.object({
    fecha: z.string().date(),
    turno_id: z.string().uuid(),
    no_opera: z.boolean(),
    hora_inicio: z.string().regex(horaTimeRegex).nullable(),
    hora_fin: z.string().regex(horaTimeRegex).nullable(),
    cruza_medianoche: z.boolean(),
});
// =============================================================================
// Programación individual (enriquecida con nombres para el grid)
// =============================================================================
export const programacionSupervisorEnriquecidaSchema = z.object({
    id: z.string().uuid(),
    semana_iso: z.string().regex(semanaIsoRegex),
    fecha: z.string().date(),
    turno_id: z.string().uuid(),
    supervisor_user_id: z.string().uuid(),
    // Nombres derivados
    supervisor_nombre: z.string(),
    supervisor_apellido: z.string(),
});
// =============================================================================
// Response GET /programacion-supervisores?semana_iso=...
// =============================================================================
export const coordinadorProgramacionSupervisoresSemanaSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    scope: z.object({
        plantas_asignadas: z.array(z.object({ id: z.string().uuid(), nombre: z.string(), codigo: z.string() })),
    }),
    semana_iso: z.string().regex(semanaIsoRegex),
    /** Fechas reales de la semana (lunes a domingo) en YYYY-MM-DD. */
    dias: z.array(z.string().date()).length(7),
    fecha_hoy: z.string().date(),
    /** Supervisores activos del tenant (todos pueden cubrir cualquier turno). */
    supervisores: z.array(supervisorResumenSchema),
    turnos: z.array(turnoResumenSchema),
    /** Filas ya creadas para esa semana. */
    programaciones: z.array(programacionSupervisorEnriquecidaSchema),
    /** Métricas derivadas para el header de la pantalla. */
    metricas: z.object({
        celdas_asignadas: z.number().int().min(0),
        /** dias × turnos (real, 21 con 7 días × 3 turnos). */
        celdas_total: z.number().int().min(0),
        /** dias × turnos × supervisores activos — "slots potenciales". */
        slots_potenciales: z.number().int().min(0),
        /** # de supervisores con ≥2 turnos en algún día de la semana. */
        conflictos_count: z.number().int().min(0),
        /** celdas_total - celdas_asignadas. */
        sin_asignar_count: z.number().int().min(0),
    }),
    /** Cantidad de turnos asignados por supervisor en esta semana (keys = supervisor.id). */
    turnos_por_supervisor: z.record(z.string().uuid(), z.number().int().min(0)),
    /** Fechas en que el supervisor tiene ≥2 turnos (keys = supervisor.id, values = fechas YYYY-MM-DD). */
    dobles_turno_por_supervisor: z.record(z.string().uuid(), z.array(z.string().date())),
    /** True si la semana inmediatamente anterior tiene ≥1 programación copiable. */
    semana_anterior_tiene_data: z.boolean(),
    /** Horario resuelto por (turno, fecha) de la semana — para deshabilitar celdas "No opera" (design §5). */
    horarios_por_fecha: z.array(horarioPorFechaSupervisoresSchema),
});
// =============================================================================
// POST input
// =============================================================================
export const programacionSupervisorCreateSchema = z.object({
    fecha: z.string().date(),
    turno_id: z.string().uuid(),
    supervisor_user_id: z.string().uuid(),
});
// =============================================================================
// POST /copiar-semana input + response
// =============================================================================
//
// Reusa el mismo patrón de líderes (clonamos en el endpoint propio para no
// crear dependencia circular entre schemas de c2 y c3).
export const programacionSupervisoresCopiarInputSchema = z.object({
    semana_iso_destino: z.string().regex(semanaIsoRegex),
    /** Opcional: si null, usa la semana inmediatamente anterior a la destino. */
    semana_iso_origen: z.string().regex(semanaIsoRegex).optional(),
});
export const programacionSupervisoresCopiarResponseSchema = z.object({
    filas_creadas: z.number().int().min(0),
    semana_iso_origen: z.string().regex(semanaIsoRegex),
    semana_iso_destino: z.string().regex(semanaIsoRegex),
    /** Asignaciones de origen omitidas porque el destino cae en un día "No opera" (design §5). */
    omitidas_dia_sin_turno: z.number().int().min(0),
});
//# sourceMappingURL=coordinador_programacion_supervisores.js.map